import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { PRODUCTS } from "../lib/products.js";
import { useI18n } from "../lib/i18n.jsx";
import Navigation from "../components/site/Navigation.jsx";
import Footer from "../components/site/Footer.jsx";
import QuoteForm from "../components/site/QuoteForm.jsx";
import FloraChat from "../components/site/FloraChat.jsx";
import { Toaster } from "../components/ui/sonner";

export default function ProductPage() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatSeed, setChatSeed] = useState("");

  const product = PRODUCTS.find(p => p.slug === slug || p.id === slug);

  useEffect(() => {
    if (product) {
      document.title = `${product.name[lang]} — EUZERONE`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", product.description[lang].slice(0, 160));
      // Canonical
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.rel = "canonical";
        document.head.appendChild(canonical);
      }
      canonical.href = `https://euzerone.com/productos/${product.slug}`;
      // Product JSON-LD
      let ld = document.getElementById("product-jsonld");
      if (!ld) {
        ld = document.createElement("script");
        ld.type = "application/ld+json";
        ld.id = "product-jsonld";
        document.head.appendChild(ld);
      }
      ld.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.name[lang],
        brand: { "@type": "Brand", name: "EUZERONE" },
        category: product.category[lang],
        description: product.description[lang],
        image: product.image,
        url: `https://euzerone.com/productos/${product.slug}`,
        sku: product.id,
        manufacturer: { "@type": "Organization", name: "EUZERONE" },
      });
    }
    return () => {
      const ld = document.getElementById("product-jsonld");
      if (ld) ld.remove();
    };
  }, [product, lang]);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7f7f4]">
        <div className="text-center">
          <h1 className="font-display text-4xl text-[#133322]">404</h1>
          <p className="text-[#5c6b61] mt-2">Producto no encontrado</p>
          <button onClick={() => nav("/")} className="eu-btn-primary mt-6">Volver al inicio</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f7f4]">
      <Navigation onOpenQuote={() => setQuoteOpen(true)} onOpenFlora={() => setChatOpen(true)} />
      <main className="pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <nav className="text-xs eu-data-label mb-8" data-testid="breadcrumb">
            <Link to="/" className="hover:text-[#133322]">INICIO</Link>
            <span className="mx-2">/</span>
            <Link to="/#productos" className="hover:text-[#133322]">PRODUCTOS</Link>
            <span className="mx-2">/</span>
            <span className="text-[#133322]">{product.name[lang]}</span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="eu-card eu-product-bg p-10 relative aspect-square flex items-center justify-center">
                <div className="absolute top-6 left-6 font-mono-eu text-[10px] font-bold bg-[#133322] text-[#f7f7f4] px-3 py-1.5 rounded-full">{product.npk}</div>
                <img src={product.image} alt={product.name[lang]} className="max-w-full max-h-full object-contain" />
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="eu-data-label mb-3">{product.category[lang]}</div>
              <h1 className="font-display text-4xl lg:text-5xl text-[#133322] leading-tight">{product.name[lang]}</h1>
              <p className="mt-3 text-lg text-[#b45f3e] italic font-display">{product.tagline[lang]}</p>
              <p className="mt-6 text-[#1c1c1c] leading-relaxed">{product.description[lang]}</p>

              <div className="mt-10">
                <div className="eu-data-label mb-4">{t.productDetail.benefits}</div>
                <ul className="grid sm:grid-cols-2 gap-2">
                  {product.benefits[lang].map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <i className="fa-solid fa-check text-[#b45f3e] mt-1 text-xs"></i><span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-10">
                <div className="eu-data-label mb-4">{t.productDetail.composition}</div>
                <div className="grid grid-cols-3 gap-2">
                  {product.composition.map((c, i) => (
                    <div key={i} className="border border-[#e5e8e2] rounded-xl p-3 bg-white">
                      <div className="font-mono-eu text-[10px] text-[#5c6b61] uppercase">{c.label}</div>
                      <div className="font-mono-eu text-sm font-bold text-[#133322] mt-1">{c.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                <div className="border border-[#e5e8e2] rounded-xl p-4 bg-white">
                  <div className="eu-data-label">{t.productDetail.foliar}</div>
                  <div className="mt-2 text-sm font-medium text-[#133322]">{product.dosage.foliar[lang]}</div>
                </div>
                <div className="border border-[#e5e8e2] rounded-xl p-4 bg-white">
                  <div className="eu-data-label">{t.productDetail.fertigation}</div>
                  <div className="mt-2 text-sm font-medium text-[#133322]">{product.dosage.fertigation[lang]}</div>
                </div>
              </div>

              <div className="mt-6">
                <div className="eu-data-label mb-3">{t.productDetail.crops}</div>
                <div className="flex flex-wrap gap-2">
                  {product.crops[lang].map((c, i) => (
                    <span key={i} className="text-xs bg-[#9ead9b]/15 text-[#133322] px-3 py-1.5 rounded-full">{c}</span>
                  ))}
                </div>
              </div>

              <div className="mt-10 flex flex-wrap gap-3">
                <button onClick={() => setQuoteOpen(true)} className="eu-btn-primary" data-testid="product-page-quote">
                  <i className="fa-solid fa-paper-plane text-xs"></i>
                  {t.productDetail.request_quote}
                </button>
                <button onClick={() => { setChatSeed(`Quiero saber más sobre ${product.name.es} para mi cultivo.`); setChatOpen(true); }} className="eu-btn-outline" data-testid="product-page-flora">
                  <i className="fa-solid fa-comment-dots text-xs"></i>
                  {t.productDetail.ask_flora}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer onOpenQuote={() => setQuoteOpen(true)} />
      <QuoteForm open={quoteOpen} onClose={() => setQuoteOpen(false)} defaultProduct={product} />
      <FloraChat open={chatOpen} onClose={() => setChatOpen(false)} seedMessage={chatSeed} />
      <Toaster richColors position="top-center" />
    </div>
  );
}
